import { apiGet } from "@/lib/api";

// ---------------------------------------------------------------------------
// Aplicação de aparência (logo, cores, rodapé, tipografia e botões) definida
// no admin. As cores são guardadas em hexadecimal e convertidas para OKLCH,
// formato usado pelas variáveis CSS do StyleSystem (--primary-*, --accent-*...).
// ---------------------------------------------------------------------------

export interface SiteTheme {
  logoUrl: string;
  logoUrlLight: string;
  logoHeight: string;
  logoHeightMobile: string;
  logoScale: string;
  logoAlign: string;
  logoBg: string;
  primaryColor: string;
  accentColor: string;
  secondaryColor: string;
  backgroundColor: string;
  footerBg: string;
  footerText: string;
  fontHeading: string;
  fontBody: string;
  fontSizeBase: string;
  buttonRadius: string;
}

export const EMPTY_THEME: SiteTheme = {
  logoUrl: "",
  logoUrlLight: "",
  logoHeight: "",
  logoHeightMobile: "",
  logoScale: "",
  logoAlign: "",
  logoBg: "",
  primaryColor: "",
  accentColor: "",
  secondaryColor: "",
  backgroundColor: "",
  footerBg: "",
  footerText: "",
  fontHeading: "",
  fontBody: "",
  fontSizeBase: "",
  buttonRadius: "",
};

// ---------------------------------------------------------------------------
// Ajustes da logo (tamanho, zoom, posição e fundo atrás dela)
// ---------------------------------------------------------------------------

export const LOGO_DEFAULT_HEIGHT = 48;
export const LOGO_DEFAULT_HEIGHT_MOBILE = 34;
export const LOGO_DEFAULT_SCALE = 100;
export const LOGO_DEFAULT_RADIUS = 8;
export const LOGO_HEIGHT_MIN = 24;
export const LOGO_HEIGHT_MAX = 80;
export const LOGO_HEIGHT_MOBILE_MIN = 20;
export const LOGO_HEIGHT_MOBILE_MAX = 80;
export const LOGO_SCALE_MIN = 60;
export const LOGO_SCALE_MAX = 160;
export const LOGO_RADIUS_MIN = 0;
export const LOGO_RADIUS_MAX = 60;

export interface ResolvedLogo {
  height: number;
  /** Altura usada no celular (menor que a do computador). */
  mobileHeight: number;
  scale: number;
  align: "left" | "center" | "right";
  /** "" = comportamento padrão do site; "transparent"; "white"; ou um hexadecimal. */
  bg: string;
  /** Cantos arredondados da logo, em pixels. */
  radius: number;
  /** Versão clara/branca da logo, usada quando a barra está sobre o banner escuro. */
  lightUrl: string;
}

// Normaliza os ajustes da logo guardados no painel para valores usáveis.
export function resolveLogo(
  theme: Pick<
    SiteTheme,
    "logoHeight" | "logoHeightMobile" | "logoScale" | "logoAlign" | "logoBg" | "logoRadius" | "logoUrlLight"
  >,
): ResolvedLogo {
  const h = Number(theme.logoHeight);
  const hm = Number(theme.logoHeightMobile);
  const s = Number(theme.logoScale);
  const rad = Number(theme.logoRadius);
  const align: ResolvedLogo["align"] =
    theme.logoAlign === "center" || theme.logoAlign === "right" ? theme.logoAlign : "left";
  return {
    height:
      Number.isFinite(h) && h >= LOGO_HEIGHT_MIN && h <= LOGO_HEIGHT_MAX ? Math.round(h) : LOGO_DEFAULT_HEIGHT,
    mobileHeight:
      Number.isFinite(hm) && hm >= LOGO_HEIGHT_MOBILE_MIN && hm <= LOGO_HEIGHT_MOBILE_MAX
        ? Math.round(hm)
        : LOGO_DEFAULT_HEIGHT_MOBILE,
    scale:
      Number.isFinite(s) && s >= LOGO_SCALE_MIN && s <= LOGO_SCALE_MAX ? Math.round(s) : LOGO_DEFAULT_SCALE,
    align,
    bg: theme.logoBg || "",
    radius:
      Number.isFinite(rad) && rad >= LOGO_RADIUS_MIN && rad <= LOGO_RADIUS_MAX
        ? Math.round(rad)
        : LOGO_DEFAULT_RADIUS,
    lightUrl: theme.logoUrlLight || "",
  };
}

// Indica se uma cor de fundo (hexadecimal) é escura o suficiente para pedir a
// versão clara da logo. Sem cor definida, consideramos escuro — é o padrão do
// rodapé do site.
export function isDarkHexColor(hex: string): boolean {
  let h = (hex || "").replace("#", "").trim();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length !== 6 || !/^[0-9a-f]{6}$/i.test(h)) return true;
  const lin = (c: number): number => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const r = lin(parseInt(h.slice(0, 2), 16) / 255);
  const g = lin(parseInt(h.slice(2, 4), 16) / 255);
  const b = lin(parseInt(h.slice(4, 6), 16) / 255);
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance < 0.45;
}

// Cor do fundo atrás da logo conforme o ajuste escolhido.
// barTransparent = true quando a barra está sobre o banner (fundo escuro).
export function logoBoxColor(bg: string, barTransparent: boolean): string {
  if (bg === "") return barTransparent ? "rgba(250, 247, 240, 0.85)" : "transparent";
  if (bg === "transparent") return "transparent";
  if (bg === "white") return "#ffffff";
  return bg;
}

// Cor do fundo atrás da logo considerando também a versão clara.
// Quando existe uma versão clara/branca e a barra está sobre o banner escuro
// (barTransparent), o "Padrão do site" deixa de desenhar a caixinha clara —
// assim a logo branca aparece limpa sobre o banner, sem o retângulo atrás.
export function logoBoxColorForBar(
  bg: string,
  barTransparent: boolean,
  hasLightLogo: boolean,
): string {
  if (barTransparent && hasLightLogo && bg === "") return "transparent";
  return logoBoxColor(bg, barTransparent);
}

interface SettingsResponse {
  ok: boolean;
  data?: {
    settings?: Record<string, string>;
  };
}

// ---------------------------------------------------------------------------
// Conversão de cor
// ---------------------------------------------------------------------------

interface Oklch {
  l: number;
  c: number;
  h: number;
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}

function hexToOklch(hex: string): Oklch {
  let h = hex.replace("#", "").trim();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;

  const lin = (x: number): number =>
    x <= 0.04045 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4);
  const lr = lin(r);
  const lg = lin(g);
  const lb = lin(b);

  const l = 0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb;
  const m = 0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb;
  const s = 0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb;

  const lc = Math.cbrt(l);
  const mc = Math.cbrt(m);
  const sc = Math.cbrt(s);

  const L = 0.2104542553 * lc + 0.793617785 * mc - 0.0040720468 * sc;
  const a = 1.9779984951 * lc - 2.428592205 * mc + 0.4505937099 * sc;
  const bb = 0.0259040371 * lc + 0.7827717662 * mc - 0.808675766 * sc;

  const C = Math.sqrt(a * a + bb * bb);
  let H = (Math.atan2(bb, a) * 180) / Math.PI;
  if (H < 0) H += 360;

  return { l: L, c: C, h: H };
}

// Retorna os canais "L C H" (sem o wrapper oklch()) de uma cor hexadecimal.
export function hexToChannels(hex: string): string {
  const { l, c, h } = hexToOklch(hex);
  return `${round3(l)} ${round3(c)} ${round3(h)}`;
}

// Faz o caminho inverso: converte canais "L C H" (OKLCH) de volta para hexadecimal.
// Usado para capturar as cores que estão aplicadas no site agora.
export function channelsToHex(channels: string): string | null {
  const parts = channels.trim().split(/\s+/).map(Number);
  if (parts.length < 3 || parts.some((n) => !Number.isFinite(n))) return null;
  const [L, C, H] = parts;
  const rad = (H * Math.PI) / 180;
  const a = C * Math.cos(rad);
  const b = C * Math.sin(rad);

  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.291485548 * b;

  const l = l_ ** 3;
  const m = m_ ** 3;
  const s = s_ ** 3;

  const lr = 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
  const lg = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
  const lb = -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s;

  const toSrgb = (x: number): number => {
    const c = Math.max(0, Math.min(1, x));
    const v = c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
    return Math.round(Math.max(0, Math.min(1, v)) * 255);
  };
  const hex = (n: number): string => n.toString(16).padStart(2, "0");
  return `#${hex(toSrgb(lr))}${hex(toSrgb(lg))}${hex(toSrgb(lb))}`;
}

const STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];
const L_RAMP = [0.95, 0.9, 0.85, 0.75, 0.6, 0.45, 0.4, 0.35, 0.3, 0.25, 0.15];
const C_FACTOR = [0.27, 0.53, 0.8, 0.93, 1.0, 1.0, 0.93, 0.8, 0.67, 0.53, 0.33];

function buildScale(hex: string): Record<string, string> {
  const { c, h } = hexToOklch(hex);
  const anchorC = Math.max(c, 0.02);
  const out: Record<string, string> = {};
  STEPS.forEach((step, i) => {
    const l = L_RAMP[i];
    const chroma = Math.min(anchorC * C_FACTOR[i], 0.35);
    out[String(step)] = `${round3(l)} ${round3(chroma)} ${round3(h)}`;
  });
  return out;
}

function applyScale(root: HTMLElement, role: string, hex: string): void {
  const scale = buildScale(hex);
  for (const [step, val] of Object.entries(scale)) {
    root.style.setProperty(`--${role}-${step}`, val);
  }
}

// ---------------------------------------------------------------------------
// Tipografia (fontes do Google carregadas sob demanda)
// ---------------------------------------------------------------------------

const RADIUS_MAP: Record<string, string> = {
  none: "0px",
  sm: "0.25rem",
  md: "0.375rem",
  lg: "0.5rem",
  full: "9999px",
};

const loadedFonts = new Set<string>();

export function loadGoogleFont(family: string): void {
  if (!family) return;
  const id = `gfont-${family.replace(/\s+/g, "-").toLowerCase()}`;
  if (loadedFonts.has(family) || document.getElementById(id)) return;
  loadedFonts.add(family);
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family).replace(/%20/g, "+")}:wght@400;500;600;700&display=swap`;
  document.head.appendChild(link);
}

export const FONT_OPTIONS: string[] = [
  "Inter",
  "Montserrat",
  "Poppins",
  "Nunito",
  "Manrope",
  "Work Sans",
  "DM Sans",
  "Raleway",
  "Barlow",
  "Lato",
  "Open Sans",
  "Source Sans 3",
  "Playfair Display",
  "Merriweather",
  "Cormorant Garamond",
];

export interface PalettePreset {
  key: string;
  name: string;
  description: string;
  primary_color: string;
  accent_color: string;
  secondary_color: string;
  background_color: string;
  footer_bg: string;
  footer_text: string;
  font_heading: string;
  font_body: string;
  font_size_base: string;
  button_radius: string;
}

export const PALETTE_PRESETS: PalettePreset[] = [
  {
    key: "verde-natura",
    name: "Verde Natura",
    description: "Verde natural com âmbar — a identidade atual, equilibrada e próxima do papelão.",
    primary_color: "#2f855a",
    accent_color: "#e0862e",
    secondary_color: "#8a6d3b",
    background_color: "#faf7f0",
    footer_bg: "#1f3d2b",
    footer_text: "#f3efe6",
    font_heading: "Montserrat",
    font_body: "Inter",
    font_size_base: "16",
    button_radius: "md",
  },
  {
    key: "terracota",
    name: "Terracota",
    description: "Terracota quente com oliva — rústico e acolhedor, ótimo para marcas artesanais.",
    primary_color: "#a8442a",
    accent_color: "#6b7f2e",
    secondary_color: "#8a5a2b",
    background_color: "#fdf6ee",
    footer_bg: "#40251c",
    footer_text: "#f7ede2",
    font_heading: "Poppins",
    font_body: "Inter",
    font_size_base: "16",
    button_radius: "md",
  },
  {
    key: "floresta",
    name: "Floresta",
    description: "Verde-floresta profundo com ocre e toque editorial nos títulos.",
    primary_color: "#1f6f43",
    accent_color: "#c08a2d",
    secondary_color: "#4d7c0f",
    background_color: "#f6faf4",
    footer_bg: "#14281b",
    footer_text: "#e8f2ea",
    font_heading: "Playfair Display",
    font_body: "Nunito",
    font_size_base: "16",
    button_radius: "lg",
  },
  {
    key: "grafite-ambar",
    name: "Grafite Âmbar",
    description: "Neutro sóbrio com âmbar — moderno, discreto e versátil.",
    primary_color: "#3f3f46",
    accent_color: "#b45309",
    secondary_color: "#71717a",
    background_color: "#f7f7f5",
    footer_bg: "#1c1c1f",
    footer_text: "#f4f4f2",
    font_heading: "Manrope",
    font_body: "Work Sans",
    font_size_base: "15",
    button_radius: "sm",
  },
];

// ---------------------------------------------------------------------------
// Captura da paleta atual (como o site está agora)
// ---------------------------------------------------------------------------

function extractFont(value: string): string {
  const first = (value.split(",")[0] ?? "").replace(/['"]/g, "").trim();
  return FONT_OPTIONS.includes(first) ? first : "";
}

const RADIUS_REVERSE: Record<string, string> = {
  "0px": "none",
  "0rem": "none",
  "0.25rem": "sm",
  "0.375rem": "md",
  "0.5rem": "lg",
  "9999px": "full",
};

// Lê as variáveis CSS aplicadas no site e devolve os valores "atuais"
// (cores, rodapé, fontes, tamanho e formato dos botões) em formato de preset.
export function captureCurrentTheme(): Partial<PalettePreset> {
  if (typeof document === "undefined") return {};
  const cs = getComputedStyle(document.documentElement);
  const get = (name: string): string => cs.getPropertyValue(name).trim();

  const size = parseInt(cs.fontSize, 10);
  const radius = get("--button-radius");

  return {
    primary_color: channelsToHex(get("--primary-500")) ?? "",
    accent_color: channelsToHex(get("--accent-500")) ?? "",
    secondary_color: channelsToHex(get("--secondary-500")) ?? "",
    background_color: channelsToHex(get("--background-50")) ?? "",
    footer_bg: channelsToHex(get("--footer-bg")) ?? "",
    footer_text: channelsToHex(get("--footer-text")) ?? "",
    font_heading: extractFont(get("--font-heading")),
    font_body: extractFont(get("--font-body")),
    font_size_base: Number.isFinite(size) ? String(size) : "",
    button_radius: RADIUS_REVERSE[radius] ?? "",
  };
}

// ---------------------------------------------------------------------------
// Aplicação no documento
// ---------------------------------------------------------------------------

export function applyTheme(theme: SiteTheme): void {
  const root = document.documentElement;

  if (theme.primaryColor) applyScale(root, "primary", theme.primaryColor);
  if (theme.accentColor) applyScale(root, "accent", theme.accentColor);
  if (theme.secondaryColor) applyScale(root, "secondary", theme.secondaryColor);
  if (theme.backgroundColor) applyScale(root, "background", theme.backgroundColor);

  if (theme.footerBg) root.style.setProperty("--footer-bg", hexToChannels(theme.footerBg));
  if (theme.footerText) root.style.setProperty("--footer-text", hexToChannels(theme.footerText));

  if (theme.fontHeading) {
    loadGoogleFont(theme.fontHeading);
    root.style.setProperty("--font-heading", `'${theme.fontHeading}', sans-serif`);
  }
  if (theme.fontBody) {
    loadGoogleFont(theme.fontBody);
    root.style.setProperty("--font-body", `'${theme.fontBody}', sans-serif`);
    root.style.setProperty("--font-label", `'${theme.fontBody}', sans-serif`);
  }

  const size = Number(theme.fontSizeBase);
  if (Number.isFinite(size) && size >= 12 && size <= 20) {
    root.style.fontSize = `${size}px`;
  }

  if (theme.buttonRadius) {
    root.style.setProperty("--button-radius", RADIUS_MAP[theme.buttonRadius] ?? RADIUS_MAP.md);
  }
}

export async function fetchSiteTheme(): Promise<SiteTheme> {
  try {
    const data = await apiGet<SettingsResponse>("/functions/v1/get-settings");
    const s = data?.data?.settings ?? {};
    return {
      logoUrl: s.logo_url ?? "",
      logoUrlLight: s.logo_url_light ?? "",
      logoHeight: s.logo_height ?? "",
      logoHeightMobile: s.logo_height_mobile ?? "",
      logoScale: s.logo_scale ?? "",
      logoAlign: s.logo_align ?? "",
      logoBg: s.logo_bg ?? "",
      primaryColor: s.primary_color ?? "",
      accentColor: s.accent_color ?? "",
      secondaryColor: s.secondary_color ?? "",
      backgroundColor: s.background_color ?? "",
      footerBg: s.footer_bg ?? "",
      footerText: s.footer_text ?? "",
      fontHeading: s.font_heading ?? "",
      fontBody: s.font_body ?? "",
      fontSizeBase: s.font_size_base ?? "",
      buttonRadius: s.button_radius ?? "",
    };
  } catch {
    return EMPTY_THEME;
  }
}

// ---------------------------------------------------------------------------
// Utilitários de cor para a prévia ao vivo
// ---------------------------------------------------------------------------

// Converte um hexadecimal para rgba() com a opacidade indicada.
// Usado apenas na prévia (elementos ilustrativos), nunca na UI real.
export function hexToRgba(hex: string, alpha: number): string {
  let h = (hex || "#000000").replace("#", "").trim();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const safe = (n: number): number => (Number.isFinite(n) ? Math.max(0, Math.min(255, n)) : 0);
  return `rgba(${safe(r)}, ${safe(g)}, ${safe(b)}, ${alpha})`;
}

// ---------------------------------------------------------------------------
// Histórico de aparência (desfazer a última paleta aplicada)
// Guardamos as últimas alterações no navegador para permitir voltar atrás.
// ---------------------------------------------------------------------------

export interface ThemeSnapshot {
  logo_url: string;
  logo_url_light: string;
  logo_height: string;
  logo_height_mobile: string;
  logo_scale: string;
  logo_align: string;
  logo_bg: string;
  logo_radius: string;
  primary_color: string;
  accent_color: string;
  secondary_color: string;
  background_color: string;
  footer_bg: string;
  footer_text: string;
  font_heading: string;
  font_body: string;
  font_size_base: string;
  button_radius: string;
  at: number;
}

const HISTORY_KEY = "real_embalagem_appearance_history";
const HISTORY_MAX = 10;

const SNAPSHOT_KEYS: Array<keyof Omit<ThemeSnapshot, "at">> = [
  "logo_url",
  "logo_url_light",
  "logo_height",
  "logo_height_mobile",
  "logo_scale",
  "logo_align",
  "logo_bg",
  "logo_radius",
  "primary_color",
  "accent_color",
  "secondary_color",
  "background_color",
  "footer_bg",
  "footer_text",
  "font_heading",
  "font_body",
  "font_size_base",
  "button_radius",
];

function sameSnapshot(
  a: Omit<ThemeSnapshot, "at">,
  b: Omit<ThemeSnapshot, "at">,
): boolean {
  return SNAPSHOT_KEYS.every((k) => (a[k] ?? "") === (b[k] ?? ""));
}

export function getAppearanceHistory(): ThemeSnapshot[] {
  if (typeof localStorage === "undefined") return [];
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as ThemeSnapshot[]) : [];
  } catch {
    return [];
  }
}

function writeAppearanceHistory(list: ThemeSnapshot[]): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(list.slice(-HISTORY_MAX)));
  } catch {
    // silencioso — histórico é um extra, não deve quebrar nada
  }
}

// Adiciona o estado atual ao histórico (chamado antes de gravar uma nova paleta).
export function pushAppearanceHistory(snapshot: Omit<ThemeSnapshot, "at">): void {
  const list = getAppearanceHistory();
  const last = list[list.length - 1];
  if (last && sameSnapshot(last, snapshot)) return;
  list.push({ ...snapshot, at: Date.now() });
  writeAppearanceHistory(list);
}

// Remove e devolve o último estado guardado (para o botão "Desfazer").
export function popAppearanceHistory(): ThemeSnapshot | null {
  const list = getAppearanceHistory();
  if (!list.length) return null;
  const last = list[list.length - 1];
  writeAppearanceHistory(list.slice(0, -1));
  return last;
}

export function clearAppearanceHistory(): void {
  try {
    localStorage.removeItem(HISTORY_KEY);
  } catch {
    // silencioso
  }
}